import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDateTime } from "@/lib/api";
import { Mail, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ds/Card";
import { PageShell, PageHeader, Section, Loading, Empty } from "@/components/ds/PageShell";
import { Stat } from "@/components/ds/Stat";
import { SeverityDot, Confidence } from "@/components/ds/Severity";

const KINDS = [
    { key: "daily", label: "Daily" },
    { key: "weekly", label: "Weekly" },
    { key: "monthly", label: "Monthly" },
];

export default function Briefings() {
    const [kind, setKind] = useState("daily");
    const [items, setItems] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);
    const [emailSettings, setEmailSettings] = useState(null);
    const [busy, setBusy] = useState(false);
    const [deliveries, setDeliveries] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const [list, settings, dels] = await Promise.all([
                api.get(`/briefings?kind=${kind}&limit=20`),
                api.get("/settings/briefings"),
                api.get("/briefings/_/deliveries"),
            ]);
            setItems(list.data);
            setEmailSettings(settings.data);
            setDeliveries(dels.data);
            if (list.data.length > 0) await openBriefing(list.data[0].id);
            else setSelected(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind]);

    const openBriefing = async (id) => {
        const { data } = await api.get(`/briefings/${id}`);
        setSelected(data);
    };

    const generateNow = async (sendEmail = false) => {
        setBusy(true);
        try {
            const { data } = await api.post("/briefings/generate", {
                kind,
                send_email: sendEmail,
            });
            toast.success(
                sendEmail
                    ? `${kind} briefing generated · email ${data.delivery?.status}`
                    : `${kind} briefing generated`,
            );
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Generation failed");
        } finally {
            setBusy(false);
        }
    };

    const sendExisting = async (briefingId) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/briefings/${briefingId}/send`);
            if (data.status === "sent") toast.success(`Email sent to ${data.recipients.join(", ")}`);
            else if (data.status === "email_not_configured")
                toast.warning("Email not configured — briefing kept in archive.");
            else toast.error(`Delivery: ${data.status} — ${data.error || ""}`);
            await load();
        } finally {
            setBusy(false);
        }
    };

    const downloadText = (briefing, text) => {
        const blob = new Blob([text], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `briefing-${briefing.kind}-${briefing.id.slice(0, 8)}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <PageShell width="wide" testId="briefings-page">
            <PageHeader
                eyebrow="Executive briefings"
                title="Briefings"
                subtitle="Every briefing is generated from the financial truth layer, what-changed engine and audit findings. Email is one channel — briefings are archived in-app regardless."
                action={
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => generateNow(false)}
                            disabled={busy}
                            className="inline-flex items-center gap-2 text-[13px] px-3 py-1.5 rounded-md text-stone-700 bg-stone-100 hover:bg-stone-200 disabled:opacity-50"
                            data-testid="generate-now"
                        >
                            <RefreshCw size={13} strokeWidth={2} />
                            {busy ? "Working…" : "Generate"}
                        </button>
                        <button
                            type="button"
                            onClick={() => generateNow(true)}
                            disabled={busy}
                            className="inline-flex items-center gap-2 text-[13px] px-3 py-1.5 rounded-md text-white disabled:opacity-50"
                            style={{ background: "var(--brand)" }}
                            data-testid="generate-and-send"
                        >
                            <Mail size={13} strokeWidth={2} />
                            Generate & email
                        </button>
                    </div>
                }
            />

            {emailSettings && (
                <Card
                    variant="quiet"
                    padding="sm"
                    className="mb-5 text-[13px]"
                    testId="email-status-banner"
                >
                    <div className="flex items-center gap-2">
                        <span
                            className={`w-1.5 h-1.5 rounded-full ${
                                emailSettings.configured ? "bg-emerald-500" : "bg-amber-500 pulse-dot"
                            }`}
                        />
                        <span className="text-stone-700">
                            {emailSettings.configured ? (
                                <>
                                    <span className="font-medium">Email delivery configured</span> ·{" "}
                                    from <span className="font-mono">{emailSettings.from_email}</span> ·
                                    {(emailSettings.recipients || []).length} recipient
                                    {(emailSettings.recipients || []).length === 1 ? "" : "s"}
                                </>
                            ) : (
                                <>
                                    <span className="font-medium">Email not configured</span> · briefings
                                    are still generated and archived. Configure Resend in Settings to enable delivery.
                                </>
                            )}
                        </span>
                    </div>
                </Card>
            )}

            <div className="inline-flex items-center gap-1 mb-5 p-1 rounded-full bg-stone-100">
                {KINDS.map((k) => (
                    <button
                        key={k.key}
                        type="button"
                        onClick={() => setKind(k.key)}
                        data-testid={`tab-${k.key}`}
                        className={`text-[13px] px-4 py-1.5 rounded-full font-medium transition ${
                            kind === k.key
                                ? "bg-white text-stone-900 shadow-sm"
                                : "text-stone-600 hover:text-stone-800"
                        }`}
                    >
                        {k.label}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card variant="default" padding="md" testId="briefing-list">
                    <div className="label-soft text-stone-500 mb-3">
                        Archive ({items.length})
                    </div>
                    {loading ? (
                        <Loading label="Loading…" />
                    ) : items.length === 0 ? (
                        <Empty title="No briefings yet" hint="Generate one above." />
                    ) : (
                        <ul className="space-y-1">
                            {items.map((b) => {
                                const isActive = selected?.briefing?.id === b.id;
                                return (
                                    <li key={b.id}>
                                        <button
                                            type="button"
                                            onClick={() => openBriefing(b.id)}
                                            className={`w-full text-left px-3 py-2.5 rounded-md transition ${
                                                isActive
                                                    ? "bg-stone-100"
                                                    : "hover:bg-stone-50"
                                            }`}
                                            data-testid={`briefing-item-${b.id.slice(0, 8)}`}
                                        >
                                            <div className="text-[13.5px] text-stone-800">
                                                {fmtDateTime(b.generated_at)}
                                            </div>
                                            <div className="text-[11px] text-stone-500 mt-0.5 flex items-center gap-2">
                                                <span className="uppercase tracking-wider font-medium">{b.kind}</span>
                                                <span>·</span>
                                                <Confidence level={b.overall_confidence} />
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </Card>

                <div className="lg:col-span-2 space-y-3" data-testid="briefing-detail">
                    {selected ? (
                        <BriefingDetail
                            selected={selected}
                            onSend={() => sendExisting(selected.briefing.id)}
                            onDownload={() => downloadText(selected.briefing, selected.text)}
                            busy={busy}
                        />
                    ) : (
                        <Card variant="default" padding="lg">
                            <Empty title="No briefing selected" hint="Pick one from the archive on the left." />
                        </Card>
                    )}
                </div>
            </div>

            {deliveries.length > 0 && (
                <Section title="Recent delivery attempts" className="mt-8">
                    <Card variant="default" padding="none" className="overflow-hidden" testId="delivery-log">
                        <table className="w-full text-[13px]">
                            <thead>
                                <tr className="text-left border-b border-stone-100 bg-stone-50/40">
                                    <th className="px-4 py-2.5 label-soft text-stone-500 font-medium">When</th>
                                    <th className="px-3 py-2.5 label-soft text-stone-500 font-medium">Kind</th>
                                    <th className="px-3 py-2.5 label-soft text-stone-500 font-medium">Recipients</th>
                                    <th className="px-4 py-2.5 label-soft text-stone-500 font-medium">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deliveries.slice(0, 12).map((d) => (
                                    <tr key={d.id} className="border-b border-stone-100 last:border-0">
                                        <td className="px-4 py-2.5 font-mono-num text-[12px] text-stone-600">{fmtDateTime(d.attempted_at)}</td>
                                        <td className="px-3 py-2.5 uppercase text-[11px] tracking-wider text-stone-700">{d.briefing_kind}</td>
                                        <td className="px-3 py-2.5 font-mono text-[12px] text-stone-700">{(d.recipients || []).join(", ") || "—"}</td>
                                        <td className="px-4 py-2.5">
                                            <DeliveryStatus status={d.status} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </Section>
            )}
        </PageShell>
    );
}

function DeliveryStatus({ status }) {
    const map = {
        sent: "bg-emerald-50 text-emerald-800 border-emerald-100",
        failed: "bg-red-50 text-red-800 border-red-100",
        email_not_configured: "bg-blue-50 text-blue-800 border-blue-100",
        no_recipients: "bg-amber-50 text-amber-800 border-amber-100",
    };
    const cls = map[status] || "bg-stone-50 text-stone-700 border-stone-100";
    return <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border rounded-full ${cls}`}>{status.replace(/_/g, " ")}</span>;
}

function BriefingDetail({ selected, onSend, onDownload, busy }) {
    const b = selected.briefing;
    const gs = b.group_summary;
    return (
        <>
            <Card variant="default" padding="lg">
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                        <div className="label-soft text-stone-500 mb-1">{b.kind} briefing</div>
                        <h2 className="text-display-sm text-stone-900">{b.period_label}</h2>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                        <button
                            type="button"
                            onClick={onDownload}
                            className="inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1.5 rounded-md text-stone-700 bg-stone-100 hover:bg-stone-200"
                            data-testid="download-briefing"
                        >
                            <Download size={12} strokeWidth={2} />
                            .txt
                        </button>
                        <button
                            type="button"
                            onClick={onSend}
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1.5 rounded-md text-white disabled:opacity-50"
                            style={{ background: "var(--brand)" }}
                            data-testid="send-briefing"
                        >
                            <Mail size={12} strokeWidth={2} />
                            Send
                        </button>
                    </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-5">
                    <Stat label="Cash" value={gs.total_cash} size="sm" />
                    <Stat label="Revenue MTD" value={gs.total_revenue_mtd} size="sm" />
                    <Stat label="NP MTD" value={gs.total_net_profit_mtd} size="sm" tone={gs.total_net_profit_mtd >= 0 ? "positive" : "negative"} />
                    <Stat label="AR" value={gs.total_ar} size="sm" />
                    <Stat label="AP" value={gs.total_ap} size="sm" />
                    <Stat label="Risk" value={`${gs.risk_score}/100`} money={false} size="sm" />
                </div>
                <div className="mt-5 pt-4 border-t border-stone-100 text-[12px] text-stone-500 flex items-center gap-3">
                    <Confidence level={b.overall_confidence} />
                    <span>·</span>
                    <span>generated {fmtDateTime(b.generated_at)}</span>
                </div>
            </Card>

            {b.headlines?.length > 0 && (
                <Card variant="default" padding="lg">
                    <div className="label-soft text-stone-500 mb-3">Headlines</div>
                    <ul className="text-[14px] text-stone-800 space-y-1.5 list-disc list-inside">
                        {b.headlines.map((h, i) => (
                            <li key={i}>{h}</li>
                        ))}
                    </ul>
                </Card>
            )}

            {b.what_changed?.high_severity_changes?.length > 0 ? (
                <Card variant="default" padding="lg">
                    <div className="label-soft text-stone-500 mb-3">What changed</div>
                    <ul className="space-y-3">
                        {b.what_changed.high_severity_changes.map((f, i) => (
                            <li key={i} className="flex items-start gap-3">
                                <SeverityDot level={f.severity} className="mt-1.5" />
                                <span className="text-[14px] text-stone-800">{f.business_impact}</span>
                            </li>
                        ))}
                    </ul>
                </Card>
            ) : !b.what_changed?.had_prior_snapshot ? (
                <Card variant="quiet" padding="lg">
                    <div className="label-soft text-stone-500 mb-2">What changed</div>
                    <div className="text-[14px] text-stone-600">
                        No comparable prior snapshot yet — the engine establishes a baseline on the first day.
                    </div>
                </Card>
            ) : null}

            {b.urgent_findings?.length > 0 && (
                <Card variant="default" padding="lg">
                    <div className="label-soft text-stone-500 mb-3">
                        Urgent findings ({b.urgent_findings.length})
                    </div>
                    <ul className="space-y-3">
                        {b.urgent_findings.slice(0, 10).map((f) => (
                            <li key={f.id} className="flex items-start gap-3">
                                <SeverityDot level={f.severity} className="mt-1.5" />
                                <div className="flex-1">
                                    <div className="text-[14px] font-medium text-stone-900">{f.title}</div>
                                    <div className="text-[12.5px] text-stone-500 mt-0.5">{f.recommended_action}</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </Card>
            )}
        </>
    );
}
